// src/utils/apiError.js
//
// What the server said, out of whatever the API layer threw.
//
// Every write helper throws `new Error(await res.text())`, so an Error's
// `message` here is the raw response body — usually `{"error": "..."}` from
// Flask, but an HTML error page from a proxy, or nothing at all when the
// request never reached a server. That is why `err.message || 'Could not …'`
// is the wrong shape and shipped a real bug: the body is rarely empty, so the
// friendly fallback never ran and an expired session put a literal
// `<!doctype html><title>401 Unauthorized</title>` on screen.
//
// Extracted here because both views of the story-elements board need it — the
// editor's Story Elements panel and the Cork Board's details panel — and each
// had grown its own copy. The comment in CorkDetailsPanel.jsx already names the
// rule the two surfaces live under: "If they ever drift in *behaviour* rather
// than in styling, that is the bug."
//
// They drifted in what they return when there is nothing to say, and that
// difference is real rather than accidental, so it stays with the callers:
// the Cork Board appends this to a sentence of its own and wants an empty
// string it can drop, while the editor's panel shows this alone and needs a
// fallback. One extraction, two framings — see `orFallback` below.

/** How much of a long body is worth putting in a banner. */
const MAX_CHARS = 200;

/**
 * The server's own explanation, or '' when it did not give a usable one.
 *
 * Best-effort by design: a banner that cannot explain the failure must still
 * show the part it does know, so every branch here degrades to '' rather than
 * throwing.
 */
export function serverExplanation(err) {
  // A non-string `message` is nothing to say, not something to stringify.
  // `err?.message || ''` guarded a missing message but not a wrongly-typed one,
  // and `.trim()` on an object threw — from the one function whose job is to
  // still work when something else has already failed. Stringifying it instead
  // was the first fix and the wrong one: `'[object Object]'` is truthy, so it
  // displaced the caller's own wording, and on the Cork Board it survived the
  // `.filter(Boolean)` that exists to drop exactly this, printing "That could
  // not be saved. [object Object]". It is noise with the content removed —
  // strictly less use than the proxy HTML two lines below, which we already
  // discard for being noise.
  const raw = typeof err?.message === 'string' ? err.message.trim() : '';
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.error) return String(parsed.error);
  } catch { /* not JSON — fall through to the raw text */ }
  // A proxy's HTML error page is noise, not an explanation.
  if (raw.startsWith('<')) return '';
  return raw.length > MAX_CHARS ? `${raw.slice(0, MAX_CHARS)}…` : raw;
}

/**
 * The server's explanation if there is one, otherwise our own wording.
 *
 * For a banner that stands alone. Where the caller already has a sentence of
 * its own to show, use `serverExplanation` directly and append it.
 */
export function orFallback(err, fallback) {
  return serverExplanation(err) || fallback;
}
